import { Controller } from "@hotwired/stimulus"
import { motionMs } from "../lib/motion"
import { trendReadout, SERIES_COLORS } from "../lib/ad_trend"

// chart.js is imported lazily (memoized at module scope, loaded once) so it is excluded from the eager
// application.js entry — it only loads on pages that actually render a trend chart (the ads dashboard).
let chartModulePromise = null
function loadChart() {
  chartModulePromise ||= import("chart.js/auto").then((module) => module.default)
  return chartModulePromise
}

// Vertical crosshair at the scrubbed day (drawn under the points, above the grid). afterEvent sees every listed event,
// even outside the chart area where onHover is skipped, so lifting the finger or leaving the canvas clears it.
const RELEASE_EVENTS = new Set([ "mouseout", "touchend", "touchcancel" ])
const crosshair = {
  id: "trendCrosshair",
  afterEvent(chart, args) {
    const type = args.event.native?.type
    if (!RELEASE_EVENTS.has(type) && args.inChartArea) return
    if (chart.$scrubIndex === null || chart.$scrubIndex === undefined) return
    chart.$scrubIndex = null
    chart.$onRelease?.()
    args.changed = true
  },
  afterDatasetsDraw(chart) {
    const index = chart.$scrubIndex
    if (index === null || index === undefined) return
    const x = chart.scales.x.getPixelForValue(index)
    const { top, bottom } = chart.chartArea
    const { ctx } = chart
    ctx.save()
    ctx.strokeStyle = "rgba(38, 30, 22, 0.35)"
    ctx.lineWidth = 1
    ctx.setLineDash([ 3, 3 ])
    ctx.beginPath()
    ctx.moveTo(x, top)
    ctx.lineTo(x, bottom)
    ctx.stroke()
    ctx.restore()
  }
}

// Ads dashboard trend chart: impressions + clicks by default; a metric tile (ad-metric-focus) focuses one series
// (the line morphs to it), and dragging across the chart scrubs a crosshair whose values show in the readout above.
export default class extends Controller {
  static values = {
    labels: Array,
    impressions: Array,
    clicks: Array,
    ctr: Array,
    names: Object,
    lazy: { type: Boolean, default: false }
  }

  async connect() {
    this.disconnected = false
    this.focused = null
    this.readout = this.element.closest("[data-trend-panel]")?.querySelector("[data-trend-readout]")
    this.Chart = await loadChart()
    if (this.disconnected) return

    // A chart from a previous connection (Turbo navigation, collapsible toggle) is replaced.
    this.Chart.getChart(this.element)?.destroy()
    this.initialized = false

    if (!this.lazyValue) this.renderChart()
  }

  disconnect() {
    this.disconnected = true
    this.chart?.destroy()
    this.chart = null
  }

  // Called by dashboard-collapsible when a lazy chart is revealed (not Stimulus' initialize(), which runs at construction).
  async reveal() {
    if (this.initialized) return
    this.Chart = await loadChart()
    if (this.disconnected) return
    this.renderChart()
  }

  focus(event) {
    this.focused = event.detail?.metric || null
    if (!this.initialized || !this.chart) return
    this.chart.data.datasets = this.datasets()
    this.chart.options.plugins.legend.display = !this.focused
    this.chart.options.animation = { duration: motionMs("EMPHASIZED_MS") }
    this.chart.update()
    this.showReadout(this.lastIndex())
  }

  series() {
    return { impressions: this.impressionsValue, clicks: this.clicksValue, ctr: this.ctrValue }
  }

  datasets() {
    const keys = this.focused ? [ this.focused ] : [ "impressions", "clicks" ]
    const series = this.series()
    return keys.map((key) => ({
      label: this.namesValue[key] || key,
      data: series[key],
      borderColor: SERIES_COLORS[key],
      backgroundColor: `${SERIES_COLORS[key]}1f`,
      fill: Boolean(this.focused),
      borderWidth: 2,
      tension: 0.4,
      pointRadius: this.focused ? 3 : 2,
      pointHoverRadius: 6
    }))
  }

  lastIndex() {
    return Math.max(0, this.labelsValue.length - 1)
  }

  showReadout(index) {
    if (!this.readout) return
    const keys = this.focused ? [ this.focused ] : [ "impressions", "clicks" ]
    this.readout.textContent = trendReadout({
      label: this.labelsValue[index],
      keys,
      names: this.namesValue,
      series: this.series(),
      index
    })
  }

  scrub(chart, event) {
    const hit = chart.getElementsAtEventForMode(event, "index", { intersect: false }, false)[0]
    const index = hit ? hit.index : null
    if (index === chart.$scrubIndex) return
    chart.$scrubIndex = index
    this.showReadout(index ?? this.lastIndex())
    chart.draw()
  }

  renderChart() {
    if (this.initialized) return
    this.initialized = true

    this.chart = new this.Chart(this.element.getContext("2d"), {
      type: "line",
      data: { labels: this.labelsValue, datasets: this.datasets() },
      plugins: [ crosshair ],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        resizeDelay: 200, // Debounce resize to prevent text rendering instability
        devicePixelRatio: 2, // Force 2x for consistent rendering across devices
        animation: { duration: 0 }, // First paint is static (no flicker while the container expands)
        events: [ "mousemove", "mouseout", "touchstart", "touchmove", "touchend", "touchcancel" ],
        onHover: (event, _elements, chart) => this.scrub(chart, event),
        plugins: {
          legend: {
            position: "bottom",
            labels: { usePointStyle: true, padding: 20, font: { size: 13, weight: "500" } }
          },
          tooltip: { enabled: false }
        },
        scales: {
          y: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: "rgba(0, 0, 0, 0.05)" } },
          x: { grid: { display: false } }
        },
        interaction: { mode: "index", axis: "x", intersect: false }
      }
    })
    this.chart.$onRelease = () => this.showReadout(this.lastIndex())
    this.showReadout(this.lastIndex())
  }
}
